import React from "react";
import { motion } from "framer-motion";
import {
  Users, FileSignature, Wallet, Clock, ShieldCheck, Lock, BarChart3, Workflow
} from "lucide-react";

const features = [
  { icon: Users,         title: "People",     subtitle: "Management",  desc: "Manage contractor teams from one workspace.",               bg: "#FFFAF0", glow: "rgba(255,200,70,0.5)",   shadow: "0 0 44px -4px rgba(240,175,40,0.42)",  border: "#E2C96A" },
  { icon: FileSignature, title: "Contracts",  subtitle: "Builder",     desc: "Generate contracts with templates and digital signatures.",  bg: "#F0FFF0", glow: "rgba(72,200,100,0.48)",  shadow: "0 0 44px -4px rgba(50,180,80,0.40)",   border: "#7ACCA0" },
  { icon: Wallet,        title: "Payroll",    subtitle: "& Payouts",   desc: "Review, approve and process payments.",                     bg: "#FFFFF0", glow: "rgba(210,205,55,0.48)",  shadow: "0 0 44px -4px rgba(195,185,35,0.40)",  border: "#CDCA6A" },
  { icon: Clock,         title: "Time",       subtitle: "Tracking",    desc: "Track work with live timers and approvals.",                bg: "#F5FFFA", glow: "rgba(50,195,175,0.48)",  shadow: "0 0 44px -4px rgba(35,180,160,0.40)",  border: "#6ECDC0" },
  { icon: ShieldCheck,   title: "Compliance", subtitle: "Monitoring",  desc: "Automatically identify labor risks and policy violations.",  bg: "#FFF5EE", glow: "rgba(255,155,75,0.5)",   shadow: "0 0 44px -4px rgba(240,135,55,0.42)",  border: "#F0A878" },
  { icon: Lock,          title: "Secure",     subtitle: "Vault",       desc: "Store sensitive documents securely.",                       bg: "#E0FFFF", glow: "rgba(50,200,225,0.48)",  shadow: "0 0 44px -4px rgba(30,185,215,0.40)",  border: "#6ECCD8" },
  { icon: BarChart3,     title: "Analytics",  subtitle: "Dashboard",   desc: "See operational health instantly.",                         bg: "#FFF0F5", glow: "rgba(205,100,180,0.44)", shadow: "0 0 44px -4px rgba(190,75,160,0.36)",  border: "#D890C8" },
  { icon: Workflow,      title: "Workflow",   subtitle: "Builder",     desc: "Create forms and automate processes.",                      bg: "#FFFAFA", glow: "rgba(190,195,220,0.44)", shadow: "0 0 44px -4px rgba(170,175,210,0.36)", border: "#C4C8DC" },
];

export default function Features() {
  return (
    <section id="features" data-testid="features-section" className="relative py-16 sm:py-24 lg:py-36 border-t border-[#0E1A1A]">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 sm:gap-6 mb-8 sm:mb-12 lg:mb-16">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="text-[11px] uppercase tracking-[0.22em] text-[#FF4F00] font-bold mb-3"
            >
              The system
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.02] font-medium max-w-3xl"
            >
              Everything your contractor operation needs.
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#9BB8B8] max-w-sm text-base"
          >
            Eight modules. One operating layer. Built to feel like a single product.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`feature-card-${i}`}
              className="group rounded-2xl border border-[#E8E8E0] relative min-h-[256px]"
              style={{ background: f.bg }}
            >
              {/* Orb — clipped inside card bounds */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                <div
                  className="absolute -top-12 -right-12 w-52 h-52 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `radial-gradient(circle, ${f.glow}, transparent 65%)` }}
                />
              </div>

              {/* Hover glow ring — box-shadow extends outside, not clipped */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{ boxShadow: `0 0 0 2px ${f.border}, 0 0 28px 8px ${f.glow}` }}
              />

              <div className="relative p-6 lg:p-7 h-full overflow-hidden">
                <div className="w-10 h-10 rounded-xl grid place-items-center bg-white/60 border border-[#E0E0DC] group-hover:border-[#FF4F00]/35 transition-colors duration-300 mb-6">
                  <f.icon className="w-[18px] h-[18px] text-[#6B7280] group-hover:text-[#FF4F00] transition-colors duration-300" />
                </div>
                <div className="font-display text-2xl lg:text-3xl font-medium tracking-[-0.025em] leading-[1.1] text-[#0A0A0B]">
                  {f.title}
                </div>
                <div className="font-display text-2xl lg:text-3xl font-medium tracking-[-0.025em] text-[#52525B] leading-[1.1] mb-3">
                  {f.subtitle}
                </div>
                <p className="text-sm text-[#71717A] leading-relaxed">{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
